/**
 * MCP tool: graph_outline
 *
 * Returns a compressed outline of a file (signatures, decorators, docstrings).
 * Resolution order:
 *   1. In-memory cache
 *   2. Pre-computed .outline.json (generated by CLI `outline` command)
 *   3. On-the-fly generation via outline module (tree-sitter or regex)
 */
import type { Database } from "../../query/db.js";
import { existsSync, readFileSync } from "node:fs";
import { OutlineCache } from "../../outline/cache.js";
import { generateOutline, formatOutlineMarkdown, formatOutlineJson } from "../../outline/index.js";
import { readJsonSafe } from "../../shared/fs.js";
import { resolveOutlinePath, type PathResolver } from "../../shared/path-resolver.js";
import type { FileOutline } from "../../shared/types.js";

const outlineCache = new OutlineCache();

export function handleOutline(
  _db: Database,
  graphDir: string,
  args: Record<string, unknown>,
  resolvePaths?: PathResolver | null,
) {
  const filePath = args.file_path as string;
  const format = (args.format as string) ?? "markdown";
  if (!filePath) return { content: [{ type: "text" as const, text: "Error: 'file_path' is required" }], isError: true };

  // 1. Cache hit
  const cached = outlineCache.get(filePath);
  if (cached) {
    const text = format === "json" ? formatOutlineJson(cached) : formatOutlineMarkdown(cached);
    return { content: [{ type: "text" as const, text }] };
  }

  // 2. Pre-computed outline
  const preComputed = resolveOutlinePath(graphDir, filePath);
  if (existsSync(preComputed)) {
    const outline = readJsonSafe<FileOutline>(preComputed);
    if (outline) {
      outlineCache.set(filePath, outline);
      const text = format === "json" ? formatOutlineJson(outline) : formatOutlineMarkdown(outline);
      return { content: [{ type: "text" as const, text }] };
    }
  }

  // 3. On-the-fly generation — resolve absolute path via path resolver
  const absolutePath = resolvePaths?.(filePath)?.absolute_path ?? null;

  if (!absolutePath || !existsSync(absolutePath)) {
    return { content: [{ type: "text" as const, text: `File not found: ${filePath}` }] };
  }

  // generateOutline is async — MCP tool handlers can return promises
  return (async () => {
    const source = readFileSync(absolutePath!, "utf-8");
    const outline = await generateOutline(filePath, source);
    if (!outline) {
      return { content: [{ type: "text" as const, text: `Unsupported language for outline: ${filePath}` }] };
    }
    outlineCache.set(filePath, outline);
    const text = format === "json" ? formatOutlineJson(outline) : formatOutlineMarkdown(outline);
    return { content: [{ type: "text" as const, text }] };
  })();
}
